import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { User as UserIcon, Truck, Mail, Hand, Satellite, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [compliance, setCompliance] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [uRes, tRes, cRes] = await Promise.all([
          api.get("/auth/users"),
          api.get("/trucks"),
          api.get("/analytics/driver-compliance").catch(() => ({ data: { rows: [] } })),
        ]);
        if (cancel) return;
        setDrivers((uRes.data || []).filter((u) => u.role === "driver"));
        setTrucks(tRes.data || []);
        setCompliance(Object.fromEntries((cRes.data?.rows || []).map((r) => [r.driver, r])));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const truckForDriver = (uid) => trucks.find((t) => t.driver_id === uid);

  const updateDriver = (uid, patch) =>
    setDrivers((list) => list.map((d) => (d.id === uid ? { ...d, ...patch } : d)));

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="drivers-page">
      <div>
        <div className="label-tag">FLEET · PEOPLE</div>
        <h1 className="heading text-3xl sm:text-4xl font-black uppercase tracking-tight">
          Drivers <span className="text-primary-orange">·</span> <span className="text-primary-orange mono text-2xl">{drivers.length}</span>
        </h1>
        <p className="text-muted-industrial text-sm mt-1">
          Drivers with mobile access to the delivery portal. "Auto by GPS" is the share of
          their status changes the system had to work out because they never pressed the button —
          the higher it is, the less they're touching the app.
        </p>
      </div>

      {loading && (
        <div className="label-tag text-primary-orange pulse-orange">Loading drivers…</div>
      )}

      {!loading && drivers.length === 0 && (
        <Card className="card-v2 p-8 text-center">
          <UserIcon className="w-8 h-8 text-primary-orange mx-auto mb-3" />
          <div className="heading text-lg font-bold uppercase">No drivers yet</div>
          <div className="text-sm text-muted-industrial mt-1">
            Invite drivers from the Fleet page — pick a truck &amp; click "Invite Driver".
          </div>
        </Card>
      )}

      {!loading && drivers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="drivers-grid">
          {drivers.map((d) => {
            const truck = truckForDriver(d.id);
            return (
              <Card key={d.id} className="card-v2 p-5 hover:border-primary-orange transition-colors" data-testid={`driver-card-${d.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-lg bg-surface-2 border border-industrial flex items-center justify-center shrink-0">
                    <UserIcon className="w-5 h-5 text-primary-orange" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="heading font-bold text-base truncate">{d.name}</div>
                    <div className="text-xs text-muted-industrial inline-flex items-center gap-1 mt-0.5 truncate">
                      <Mail className="w-3 h-3" /> {d.email}
                    </div>
                    <div className="mt-3 pt-3 border-t border-industrial flex items-center gap-2 text-xs">
                      <Truck className="w-3.5 h-3.5 text-primary-orange" />
                      {truck ? (
                        <span className="mono uppercase tracking-widest text-[11px]">
                          Truck <span className="text-primary-orange font-bold">{truck.number}</span> · {truck.status?.replace("_", " ")}
                        </span>
                      ) : (
                        <span className="mono uppercase text-muted-industrial text-[11px]">Unassigned</span>
                      )}
                    </div>
                    {(() => {
                      const c = compliance[d.name];
                      if (!c || !c.lifetime_dockets) {
                        return (
                          <div className="mt-2 mono text-[10px] uppercase tracking-widest text-muted-industrial" data-testid={`driver-compliance-${d.id}`}>
                            No loads on record
                          </div>
                        );
                      }
                      if (!c.dockets) {
                        return (
                          <div className="mt-2 mono text-[10px] uppercase tracking-widest text-muted-industrial" data-testid={`driver-compliance-${d.id}`}>
                            No loads in the last 30 days
                            <div className="normal-case tracking-normal mt-0.5">
                              Last load {c.last_load_at || "—"} · {c.lifetime_dockets} load{c.lifetime_dockets === 1 ? "" : "s"} all time
                            </div>
                          </div>
                        );
                      }
                      if (!c.events) {
                        return (
                          <div className="mt-2 mono text-[10px] uppercase tracking-widest text-muted-industrial" data-testid={`driver-compliance-${d.id}`}>
                            {c.dockets} load{c.dockets === 1 ? "" : "s"} · 30 days
                            <div className="normal-case tracking-normal mt-0.5">No GPS status history yet</div>
                          </div>
                        );
                      }
                      const hot = c.auto_pct >= 60;
                      return (
                        <div className="mt-2" data-testid={`driver-compliance-${d.id}`}>
                          <div className="flex items-center justify-between mono text-[10px] uppercase tracking-widest">
                            <span className="text-muted-industrial inline-flex items-center gap-1"><Satellite className="w-3 h-3" /> Auto by GPS</span>
                            <span className={`font-black ${hot ? "text-red-500" : "text-emerald-500"}`} data-testid={`driver-auto-pct-${d.id}`}>{c.auto_pct}%</span>
                          </div>
                          <div className="mt-1 h-1.5 bg-surface-2 border border-industrial overflow-hidden">
                            <div className={`h-full ${hot ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, c.auto_pct)}%` }} />
                          </div>
                          <div className="mt-1 mono text-[10px] text-muted-industrial inline-flex items-center gap-1">
                            <Hand className="w-3 h-3" /> {c.manual} pressed · {c.auto} automatic · {c.dockets} load{c.dockets === 1 ? "" : "s"}
                            {c.prompted ? ` · ${c.prompted} needed a nag` : ""}
                          </div>
                        </div>
                      );
                    })()}
                    <DriverPin driver={d} onChange={(patch) => updateDriver(d.id, patch)} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DriverPin({ driver, onChange }) {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!/^\d{4}$/.test(pin)) return toast.error("PIN must be exactly 4 digits");
    setBusy(true);
    try {
      await api.put(`/drivers/${driver.id}/pin`, { pin });
      onChange({ has_pin: true });
      toast.success(`PIN set for ${driver.name}`);
      setOpen(false);
      setPin("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not set PIN");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/drivers/${driver.id}/pin`);
      onChange({ has_pin: false });
      toast.success(`PIN removed for ${driver.name}`);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove PIN");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mt-3 pt-3 border-t border-industrial flex items-center justify-between">
        <div className="mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5" data-testid={`driver-pin-status-${driver.id}`}>
          <KeyRound className="w-3.5 h-3.5 text-primary-orange" />
          {driver.has_pin ? (
            <span className="text-emerald-500 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> PIN set</span>
          ) : (
            <span className="text-muted-industrial">No PIN</span>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => { setPin(""); setOpen(true); }}
          data-testid={`driver-set-pin-btn-${driver.id}`}
          className="rounded-none border-industrial uppercase text-[10px] tracking-widest h-7"
        >
          {driver.has_pin ? "Change PIN" : "Set PIN"}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPin(""); }}>
        <DialogContent className="bg-surface border-industrial max-w-sm" data-testid={`driver-pin-dialog-${driver.id}`}>
          <DialogHeader>
            <DialogTitle className="heading uppercase flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-primary-orange" /> {driver.name} · 4-digit PIN
            </DialogTitle>
          </DialogHeader>
          <p className="mono text-[11px] text-muted-industrial">
            This is the PIN {driver.name} taps on their phone to sign in to the Driver App. No email or password needed.
          </p>
          <Input
            data-testid={`driver-pin-input-${driver.id}`}
            type="tel"
            inputMode="numeric"
            maxLength={4}
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="● ● ● ●"
            className="rounded-none border-industrial mono text-center text-2xl tracking-[0.5em] h-14"
          />
          <DialogFooter className="sm:justify-between gap-2">
            {driver.has_pin ? (
              <Button variant="outline" onClick={remove} disabled={busy} data-testid={`driver-remove-pin-btn-${driver.id}`} className="rounded-none border-red-500/40 text-red-400 hover:text-red-300 uppercase text-xs">
                Remove PIN
              </Button>
            ) : <span />}
            <Button onClick={save} disabled={busy || pin.length !== 4} data-testid={`driver-pin-save-btn-${driver.id}`} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">
              {busy ? "Saving…" : "Save PIN"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
